/** FARBAR "AS IS" contract generator (Tom 2026-08-12: "send farbar contracts
 * quickly to people ... no need to use boldsign").
 *
 * The backend fills Tom's own uploaded blank FARBAR PDF with the deal terms
 * and, when "sign it" is on, overlays his typed signature/initials — then
 * streams back the finished PDF. Multipart uploads (template, signature font)
 * go through raw fetch like esign/api.ts so the boundary isn't clobbered;
 * everything else uses backendFetch. */
import { API_BASE } from "../../config";
import { BackendError, backendFetch } from "../../lib/backend";

/** The term keys the generator understands (mapped to the form server-side). */
export interface FarbarValues {
  sellerName?: string;
  buyerCompany?: string;
  streetAddress?: string;
  county?: string;
  parcelId?: string;
  purchasePrice?: string;
  initialDeposit?: string;
  titleCompany?: string;
  titleAddress?: string;
  titlePhone?: string;
  titleEmail?: string;
  closingDate?: string;
  inspectionDays?: string;
  additionalTerms?: string;
  listingAssociate?: string;
  listingBroker?: string;
  signDate?: string;
  // checkbox terms — "1" when ticked (drawn as an X on the form)
  cashSale?: string;
  buyerClosingAgent?: string;
  mayAssign?: string;
}

/** What a property pre-fills into the modal (the org defaults fill the rest). */
export interface FarbarSeed {
  sellerName?: string;
  streetAddress?: string;
  county?: string;
  parcelId?: string;
  purchasePrice?: string;
  listingAssociate?: string;
  listingBroker?: string;
}

/** Build the modal's seed from a lead.
 *
 * SELLER = the owner of record: the typed "owner" field, else the county
 * appraiser's owner (lead.facts.owner = raw.county_owner). For a Zillow lead
 * it is NEVER lead.seller — that string is the listing agent · brokerage
 * (Tom 2026-10-13, 8148 Cyers Lane: "The sellers name isnt correct" — the
 * modal had put "Janet Quinones · MAGNUS REALTY GROUP LLC" on the seller
 * line); the agent and broker go on the FARBAR listing lines instead. For a
 * wholesaler lead (InvestorLift / SMS) lead.seller is the wholesaler, who is
 * the counter-party on an assignment, so it stays the fallback there. */
export function farbarSeedFor(
  lead: { source: string | null; seller: string | null; address: string; displayAddress?: string;
          county: string | null; price: number | null; facts?: Record<string, string> },
  fields: Record<string, unknown> | null | undefined,
): FarbarSeed {
  const typed = (k: string) => (typeof fields?.[k] === "string" ? (fields[k] as string).trim() : "");
  const isZillow = /zillow/i.test(lead.source ?? "");
  const owner = typed("owner") || (lead.facts?.owner ?? "").trim();
  const agentCo = (lead.seller ?? "").split("·").map((s) => s.trim());
  return {
    sellerName: owner || (isZillow ? "" : (lead.seller ?? "")),
    streetAddress: lead.displayAddress || lead.address,
    county: (lead.county ?? "").replace(/\s*county\s*$/i, ""),
    parcelId: typed("parcel") || (lead.facts?.parcel ?? "").trim(),
    purchasePrice: lead.price != null
      ? lead.price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : "",
    // Zillow's listing agent · brokerage lands in lead.seller — split it onto
    // the FARBAR listing lines (IL rows carry a wholesaler here, so only
    // Zillow-sourced leads seed these).
    listingAssociate: isZillow ? (agentCo[0] ?? "") : "",
    listingBroker: isZillow ? (agentCo[1] ?? "") : "",
  };
}

export interface FarbarStatus {
  hasTemplate: boolean;
  nFields: number;
  hasSignFont: boolean;
}

export async function farbarStatus(token: string | null): Promise<FarbarStatus> {
  try {
    const j = await backendFetch<Record<string, unknown>>(token, "/api/farbar/status", { timeoutMs: 20000 });
    return {
      hasTemplate: j?.["hasTemplate"] === true,
      nFields: typeof j?.["nFields"] === "number" ? (j["nFields"] as number) : 0,
      hasSignFont: j?.["hasSignFont"] === true,
    };
  } catch {
    return { hasTemplate: false, nFields: 0, hasSignFont: false };
  }
}

export interface FarbarDefaults {
  defaults: FarbarValues;
  signName: string;
  signInitials: string;
}

export async function getFarbarDefaults(token: string | null): Promise<FarbarDefaults> {
  try {
    const j = await backendFetch<Record<string, unknown>>(token, "/api/farbar/defaults", { timeoutMs: 20000 });
    return {
      defaults: (j?.["defaults"] as FarbarValues) ?? {},
      signName: typeof j?.["signName"] === "string" ? (j["signName"] as string) : "",
      signInitials: typeof j?.["signInitials"] === "string" ? (j["signInitials"] as string) : "",
    };
  } catch {
    return { defaults: {}, signName: "", signInitials: "" };
  }
}

export async function saveFarbarDefaults(token: string | null, d: FarbarDefaults): Promise<void> {
  await backendFetch(token, "/api/farbar/defaults", {
    method: "PUT",
    body: { defaults: d.defaults, signName: d.signName, signInitials: d.signInitials },
    timeoutMs: 25000,
  });
}

/** Multipart upload of the blank FARBAR PDF (admin). Returns the field count. */
async function uploadMultipart(token: string | null, path: string, file: File): Promise<Record<string, unknown>> {
  const fd = new FormData();
  fd.append("file", file, file.name);
  if (token) fd.append("token", token);
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), 120000);
  try {
    const res = await fetch(API_BASE + path, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
      signal: ctl.signal,
    });
    const text = await res.text();
    let data: unknown = null;
    try { data = text ? JSON.parse(text) : null; } catch { data = text; }
    if (!res.ok) {
      const detail = data !== null && typeof data === "object" && typeof (data as { detail?: unknown }).detail === "string"
        ? (data as { detail: string }).detail
        : String(res.status);
      throw new BackendError(res.status, detail.slice(0, 160));
    }
    return (data as Record<string, unknown>) ?? {};
  } finally {
    clearTimeout(t);
  }
}

export const uploadFarbarTemplate = (token: string | null, file: File) =>
  uploadMultipart(token, "/api/farbar/template", file);
export const uploadFarbarSignFont = (token: string | null, file: File) =>
  uploadMultipart(token, "/api/farbar/signfont", file);

/** Generate the filled (optionally signed) contract and hand back a Blob so the
 * caller can download or attach it. Raw fetch — the response is a PDF, not JSON. */
export async function generateFarbar(
  token: string | null,
  values: FarbarValues,
  sign: boolean,
  filename?: string,
): Promise<Blob> {
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), 120000);
  try {
    const res = await fetch(API_BASE + "/api/farbar/generate", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ values, sign, filename: filename ?? null }),
      signal: ctl.signal,
    });
    if (!res.ok) {
      const text = await res.text();
      let detail = String(res.status);
      try {
        const j = JSON.parse(text);
        if (typeof j?.detail === "string") detail = j.detail;
      } catch { /* keep status */ }
      throw new BackendError(res.status, detail.slice(0, 160));
    }
    return await res.blob();
  } finally {
    clearTimeout(t);
  }
}

/** Trigger a browser download of a generated PDF blob. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.toLowerCase().endsWith(".pdf") ? filename : filename + ".pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
